import React, { useState, useRef, useEffect } from 'react';
import { FaQuestionCircle, FaTimes, FaPaperPlane } from 'react-icons/fa';
import '../styles/HelpChatWidget.css';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

const HelpChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll al último mensaje
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Mensaje de bienvenida
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: '¡Hola! 👋 Soy tu asistente de ayuda de Grupo Sarmiento. Pregúntame cualquier cosa sobre cómo usar el sistema.',
        timestamp: new Date()
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const screen = window.location.pathname;

      const response = await fetch(`${API_URL}/api/ai/help`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: inputMessage,
          screen: screen
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al obtener respuesta');
      }

      const assistantMessage = {
        role: 'assistant',
        content: data.response,
        sources: data.sources,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      
      const errorMessage = {
        role: 'assistant',
        content: `Lo siento, ocurrió un error: ${error.message}. Por favor, intenta nuevamente.`,
        isError: true,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Botón flotante */}
      {!isOpen && (
        <button 
          className="help-chat-button"
          onClick={() => setIsOpen(true)}
          title="Asistente de ayuda"
        >
          <FaQuestionCircle size={24} />
        </button>
      )}

      {/* Widget de chat */}
      {isOpen && (
        <div className="help-chat-widget">
          {/* Header */}
          <div className="help-chat-header">
            <div className="help-chat-title">
              <FaQuestionCircle className="help-chat-icon" />
              <span>Asistente de Ayuda</span>
            </div>
            <button 
              className="help-chat-close"
              onClick={() => setIsOpen(false)}
            >
              <FaTimes />
            </button>
          </div>

          {/* Mensajes */}
          <div className="help-chat-messages">
            {messages.map((msg, index) => (
              <div 
                key={index}
                className={`help-chat-message ${msg.role} ${msg.isError ? 'error' : ''}`}
              >
                <div className="message-content">
                  {msg.content}
                </div>
                {msg.sources && msg.sources.length > 0 && (
                  <div className="message-sources">
                    <small>
                      📚 Fuentes: {msg.sources.length} referencia(s) del manual
                    </small>
                  </div>
                )}
                <div className="message-time">
                  {msg.timestamp.toLocaleTimeString('es-AR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="help-chat-message assistant loading">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="help-chat-input">
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe tu pregunta..."
              rows={2}
              disabled={isLoading}
            />
            <button 
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              className="send-button"
            >
              <FaPaperPlane />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpChatWidget;
